/* «Тохиргоо» — үйлчилгээ + угаагчийн бүртгэл + QPay холболт */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cwApi, type CwServiceDto, type CwWasherDto } from '@/api/carwash';
import { tug } from '@/shared/format';
import { IconBank, IconCheck, IconPencil, IconPlus, IconQr, IconTrash, IconX } from '@/shared/icons';
import { useToast } from '@/app/providers/ToastProvider';
import { useShellUi } from '@/features/shell/ShellLayout';
import { PlanPanel } from './PlanPanel';

const PRICE_COLS = [
  { key: 'PriceSedan', label: 'Суудлын' },
  { key: 'PriceJeep', label: 'Жийп' },
  { key: 'PriceTruck', label: 'Том оврын' },
  { key: 'PriceVan', label: 'Микроавтобус' },
] as const;

interface ServiceForm {
  Name: string;
  PriceSedan: string;
  PriceJeep: string;
  PriceTruck: string;
  PriceVan: string;
  IsActive: boolean;
}

const emptyServiceForm: ServiceForm = {
  Name: '', PriceSedan: '', PriceJeep: '', PriceTruck: '', PriceVan: '', IsActive: true,
};

function num(v: string): number {
  return Math.max(0, parseInt(v.replace(/\D/g, ''), 10) || 0);
}

function ServiceEditor({ initial, onSave, onCancel, busy }: {
  initial: ServiceForm;
  onSave: (f: ServiceForm) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [f, setF] = useState(initial);
  const set = (patch: Partial<ServiceForm>) => setF((p) => ({ ...p, ...patch }));
  const canSave = f.Name.trim().length > 0 && !busy;
  return (
    <div className="rounded-[var(--r)] bg-cyan-50/50 ring-2 ring-[var(--brand2)] p-3 flex flex-col gap-2.5">
      <input value={f.Name} onChange={(e) => set({ Name: e.target.value })} placeholder="Үйлчилгээний нэр"
        autoFocus
        className="w-full h-10 px-3 rounded-[9px] bg-white ring-1 ring-slate-200 text-[14px] font-semibold text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)]" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PRICE_COLS.map((c) => (
          <label key={c.key} className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{c.label}</span>
            <input value={f[c.key]} inputMode="numeric"
              onChange={(e) => set({ [c.key]: e.target.value.replace(/\D/g, '').slice(0, 7) } as Partial<ServiceForm>)}
              placeholder="0"
              className="h-9 px-2.5 rounded-[9px] bg-white ring-1 ring-slate-200 text-right text-[13px] font-semibold text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)]" />
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <label className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-600 cursor-pointer">
          <input type="checkbox" checked={f.IsActive} onChange={(e) => set({ IsActive: e.target.checked })}
            className="w-4 h-4 accent-[var(--brand1)]" />
          Идэвхтэй
        </label>
        <div className="ml-auto flex gap-1.5">
          <button onClick={onCancel} className="h-9 px-3.5 rounded-[9px] text-[13px] font-semibold text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition">
            Болих
          </button>
          <button onClick={() => canSave && onSave(f)} disabled={!canSave}
            className={'h-9 px-4 rounded-[9px] text-[13px] font-semibold inline-flex items-center gap-1.5 transition ' +
              (canSave ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95' : 'text-slate-400 bg-slate-100 cursor-not-allowed')}>
            <IconCheck size={14} strokeWidth={2.5} /> Хадгалах
          </button>
        </div>
      </div>
    </div>
  );
}

function ServicesPanel() {
  const toast = useToast();
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['cw', 'services'], queryFn: cwApi.services });
  const [editing, setEditing] = useState<string | 'new' | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['cw', 'services'] });
  const onError = (e: unknown) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.');

  const saveMut = useMutation({
    mutationFn: (vars: { id: string | null; f: ServiceForm }) => {
      const body = {
        Name: vars.f.Name.trim(),
        PriceSedan: num(vars.f.PriceSedan),
        PriceJeep: num(vars.f.PriceJeep),
        PriceTruck: num(vars.f.PriceTruck),
        PriceVan: num(vars.f.PriceVan),
        IsActive: vars.f.IsActive,
        SortOrder: 0,
      };
      return vars.id ? cwApi.updateService(vars.id, body) : cwApi.createService(body);
    },
    onSuccess: () => { invalidate(); setEditing(null); toast('Хадгалагдлаа'); },
    onError,
  });
  const delMut = useMutation({
    mutationFn: (id: string) => cwApi.deleteService(id),
    onSuccess: () => { invalidate(); toast('Устгагдлаа'); },
    onError,
  });

  const toForm = (s: CwServiceDto): ServiceForm => ({
    Name: s.Name,
    PriceSedan: String(s.PriceSedan), PriceJeep: String(s.PriceJeep),
    PriceTruck: String(s.PriceTruck), PriceVan: String(s.PriceVan),
    IsActive: s.IsActive,
  });

  return (
    <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-3">
      <div className="flex items-center">
        <div className="text-[14px] font-bold text-slate-800">Үйлчилгээ ба үнэ</div>
        <button onClick={() => setEditing('new')}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-[9px] text-[13px] font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95 transition">
          <IconPlus size={15} strokeWidth={2.5} /> Нэмэх
        </button>
      </div>

      {editing === 'new' && (
        <ServiceEditor initial={emptyServiceForm} busy={saveMut.isPending}
          onSave={(f) => saveMut.mutate({ id: null, f })} onCancel={() => setEditing(null)} />
      )}

      <div className="flex flex-col gap-2">
        {(listQ.data ?? []).map((s) =>
          editing === s.Id ? (
            <ServiceEditor key={s.Id} initial={toForm(s)} busy={saveMut.isPending}
              onSave={(f) => saveMut.mutate({ id: s.Id, f })} onCancel={() => setEditing(null)} />
          ) : (
            <div key={s.Id} className={'rounded-[var(--r)] ring-1 ring-slate-200 p-3 flex flex-col gap-2 ' + (s.IsActive ? 'bg-white' : 'bg-slate-50 opacity-60')}>
              <div className="flex items-center gap-2">
                <span className="text-[14px] font-bold text-slate-900">{s.Name}</span>
                {!s.IsActive && <span className="text-[10px] font-bold text-slate-400 bg-slate-200/70 rounded-full px-2 py-0.5">Идэвхгүй</span>}
                <div className="ml-auto flex gap-1">
                  <button onClick={() => setEditing(s.Id)} title="Засах"
                    className="w-8 h-8 rounded-[8px] flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
                    <IconPencil size={14} />
                  </button>
                  <button onClick={() => delMut.mutate(s.Id)} title="Устгах"
                    className="w-8 h-8 rounded-[8px] flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition">
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                {PRICE_COLS.map((c) => (
                  <div key={c.key} className="rounded-[8px] bg-slate-50 ring-1 ring-slate-100 px-2.5 py-1.5">
                    <div className="text-[10px] text-slate-400 font-semibold">{c.label}</div>
                    <div className="text-[13px] font-bold text-slate-900">{tug(s[c.key])}</div>
                  </div>
                ))}
              </div>
            </div>
          ),
        )}
        {listQ.isLoading && <div className="text-[13px] text-slate-400 py-4 text-center">Ачааллаж байна…</div>}
      </div>
    </div>
  );
}

function WashersPanel() {
  const toast = useToast();
  const qc = useQueryClient();
  const listQ = useQuery({ queryKey: ['cw', 'washers'], queryFn: cwApi.washers });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['cw', 'washers'] });
  const onError = (e: unknown) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.');

  const createMut = useMutation({
    mutationFn: () => cwApi.createWasher({ Name: name.trim(), Phone: phone.trim() || undefined, IsActive: true }),
    onSuccess: () => { invalidate(); setName(''); setPhone(''); toast('Угаагч нэмэгдлээ'); },
    onError,
  });
  const toggleMut = useMutation({
    mutationFn: (w: CwWasherDto) =>
      cwApi.updateWasher(w.Id, { Name: w.Name, Phone: w.Phone || undefined, IsActive: !w.IsActive }),
    onSuccess: invalidate,
    onError,
  });
  const delMut = useMutation({
    mutationFn: (id: string) => cwApi.deleteWasher(id),
    onSuccess: () => { invalidate(); toast('Устгагдлаа'); },
    onError,
  });

  const canAdd = name.trim().length > 0 && !createMut.isPending;

  return (
    <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-3">
      <div className="text-[14px] font-bold text-slate-800">Угаагчид</div>

      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Нэр"
          className="flex-1 h-10 px-3 rounded-[9px] bg-slate-50 ring-1 ring-slate-200 text-[14px] font-semibold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
        <input value={phone} inputMode="numeric"
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Утас"
          className="w-[110px] h-10 px-3 rounded-[9px] bg-slate-50 ring-1 ring-slate-200 text-[14px] font-semibold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
        <button onClick={() => canAdd && createMut.mutate()} disabled={!canAdd}
          className={'h-10 px-4 rounded-[9px] text-[13px] font-semibold inline-flex items-center gap-1.5 transition ' +
            (canAdd ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95' : 'text-slate-400 bg-slate-100 cursor-not-allowed')}>
          <IconPlus size={15} strokeWidth={2.5} /> Нэмэх
        </button>
      </div>

      <div className="flex flex-col gap-1.5">
        {(listQ.data ?? []).map((w) => (
          <div key={w.Id} className={'flex items-center gap-2.5 rounded-[var(--r)] ring-1 ring-slate-200 px-3 py-2.5 ' + (w.IsActive ? 'bg-white' : 'bg-slate-50 opacity-60')}>
            <span className={'w-8 h-8 rounded-full text-[13px] font-bold text-white flex items-center justify-center ' + (w.IsActive ? 'bg-[var(--brand2)]' : 'bg-slate-400')}>
              {w.Name[0]}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[14px] font-bold text-slate-900">{w.Name}</div>
              <div className="text-[12px] text-slate-400">
                {w.Phone || '—'} · одоо {w.ActiveCarCount} машин · нийт {w.TotalWashedCars} угаасан
              </div>
            </div>
            <button onClick={() => toggleMut.mutate(w)}
              className={'h-8 px-3 rounded-full text-[12px] font-semibold ring-1 transition ' +
                (w.IsActive
                  ? 'text-emerald-700 bg-emerald-50 ring-emerald-200 hover:bg-emerald-100'
                  : 'text-slate-500 bg-slate-100 ring-slate-200 hover:bg-slate-200')}>
              {w.IsActive ? 'Идэвхтэй' : 'Идэвхгүй'}
            </button>
            <button onClick={() => delMut.mutate(w.Id)} title="Устгах"
              className="w-8 h-8 rounded-[8px] flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition">
              <IconX size={15} />
            </button>
          </div>
        ))}
        {listQ.isLoading && <div className="text-[13px] text-slate-400 py-4 text-center">Ачааллаж байна…</div>}
      </div>
    </div>
  );
}

/* QPay (wire.mn) холболт — банкны данс бүртгээд хүсэлт илгээнэ, admin token-оор идэвхжүүлнэ */
function QpayPanel() {
  const toast = useToast();
  const qc = useQueryClient();
  const [bankId, setBankId] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  const statusQ = useQuery({ queryKey: ['cw', 'qpay'], queryFn: cwApi.qpayStatus });
  const banksQ = useQuery({ queryKey: ['cw', 'banks'], queryFn: cwApi.banks });
  const accountsQ = useQuery({ queryKey: ['cw', 'bank-accounts'], queryFn: cwApi.bankAccounts });

  const status = statusQ.data;
  const accounts = accountsQ.data ?? [];
  const onError = (e: unknown) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.');

  const createAccountMut = useMutation({
    // Эхлээд tsetsu.mn-ийн банкны шалгуураар (Голомт API) дансыг заавал шалгана —
    // олдсон тохиолдолд л баталгаажсан нэрээр нь бүртгэнэ.
    mutationFn: async () => {
      const found = await cwApi.lookupBankAccount({ FKBankId: bankId, Value: accountNo.trim() });
      if (!found?.AccountName)
        throw new Error('Данс банкны лавлагаанаас олдсонгүй — дугаараа шалгана уу.');
      return cwApi.createBankAccount({
        FKBankId: bankId,
        BankAccountNo: found.AccountIban || accountNo.trim(),
        BankAccountName: found.AccountName,
        IsActive: true,
        IsDefault: accounts.length === 0,
      });
    },
    onSuccess: (dto) => {
      qc.invalidateQueries({ queryKey: ['cw', 'bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['cw', 'qpay'] });
      setBankId(''); setAccountNo('');
      setSelectedAccountId(dto.Id);
      toast(`Данс баталгаажиж бүртгэгдлээ · ${dto.BankAccountName || ''}`);
    },
    onError,
  });

  const requestMut = useMutation({
    mutationFn: (accountId: string) => cwApi.qpayRequest(accountId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cw', 'qpay'] });
      toast('QPay холбуулах хүсэлт илгээгдлээ');
    },
    onError,
  });

  const canAddAccount = !!bankId && accountNo.trim().length > 0 && !createAccountMut.isPending;
  const hasConnection = status?.Status != null;
  const isActive = status?.Status === 1;
  const isPending = status?.Status === 0;

  const badge = isActive
    ? <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-3 py-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>Идэвхтэй</span>
    : isPending
      ? <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-full px-3 py-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>Хүлээгдэж буй</span>
      : <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 bg-slate-100 ring-1 ring-slate-200 rounded-full px-3 py-1.5">Холбоогүй</span>;

  return (
    <div className="rounded-[var(--r)] bg-white ring-1 ring-slate-900/5 shadow-xs p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-[9px] bg-cyan-50 text-[var(--brand1)] flex items-center justify-center"><IconQr size={16} /></span>
        <div className="text-[14px] font-bold text-slate-800">QPay холболт</div>
        <div className="ml-auto">{badge}</div>
      </div>

      {hasConnection && (
        <div className="rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-100 px-3.5 py-3 flex items-center gap-2.5">
          <IconBank size={16} className="text-slate-400 shrink-0" />
          <div className="leading-tight min-w-0 flex-1">
            <div className="text-[13px] font-bold text-slate-900">{status?.BankName || 'Банк'} · {status?.BankAccountNo || ''}</div>
            <div className="text-[12px] text-slate-400">
              {isActive ? 'QPay төлбөр хүлээн авахад бэлэн' : 'Админ token баталгаажуулсны дараа идэвхжинэ'}
            </div>
          </div>
        </div>
      )}

      {!hasConnection && (
        <>
          {/* Данс бүртгэх */}
          <div className="flex flex-col gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Банкны данс</div>
            {accounts.map((a) => (
              <label key={a.Id} className={'flex items-center gap-2.5 rounded-[var(--r)] ring-1 px-3 py-2.5 cursor-pointer transition ' +
                (selectedAccountId === a.Id ? 'bg-cyan-50 ring-2 ring-[var(--brand2)]' : 'bg-white ring-slate-200 hover:ring-slate-300')}>
                <input type="radio" name="qpay-account" checked={selectedAccountId === a.Id}
                  onChange={() => setSelectedAccountId(a.Id)} className="w-4 h-4 accent-[var(--brand1)]" />
                <div className="leading-tight min-w-0 flex-1">
                  <div className="text-[13px] font-bold text-slate-900">{a.BankName || 'Банк'} · {a.BankAccountNo}</div>
                  <div className="text-[12px] text-slate-400">{a.BankAccountName || '—'}</div>
                </div>
              </label>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <select value={bankId} onChange={(e) => setBankId(e.target.value)}
                className="h-10 px-3 rounded-[9px] bg-slate-50 ring-1 ring-slate-200 text-[13px] font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--brand2)]">
                <option value="">Банк сонгох…</option>
                {(banksQ.data ?? []).map((b) => (
                  <option key={b.Id} value={b.Id}>{b.BankName}</option>
                ))}
              </select>
              <input value={accountNo} onChange={(e) => setAccountNo(e.target.value.replace(/[^\dA-Za-z]/g, '').slice(0, 50))}
                placeholder="Дансны дугаар эсвэл MN IBAN"
                className="h-10 px-3 rounded-[9px] bg-slate-50 ring-1 ring-slate-200 text-[13px] font-semibold text-slate-900 placeholder:text-slate-300 placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
            </div>
            <button onClick={() => canAddAccount && createAccountMut.mutate()} disabled={!canAddAccount}
              className={'self-start h-9 px-4 rounded-[9px] text-[13px] font-semibold inline-flex items-center gap-1.5 transition ' +
                (canAddAccount ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95' : 'text-slate-400 bg-slate-100 cursor-not-allowed')}>
              <IconPlus size={15} strokeWidth={2.5} />
              {createAccountMut.isPending ? 'Шалгаж байна…' : 'Данс бүртгэх'}
            </button>
            <div className="text-[11px] text-slate-400">
              Данс банкны лавлагаагаар (Голомт API) заавал шалгагдаж, эзэмшигчийн нэр автоматаар баталгаажна
            </div>
          </div>

          {/* Хүсэлт илгээх */}
          <button onClick={() => selectedAccountId && requestMut.mutate(selectedAccountId)}
            disabled={!selectedAccountId || requestMut.isPending}
            className={'min-h-[44px] rounded-[var(--r)] text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition ' +
              (selectedAccountId && !requestMut.isPending
                ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95'
                : 'text-slate-400 bg-slate-100 cursor-not-allowed')}>
            <IconQr size={16} strokeWidth={2.2} />
            {requestMut.isPending ? 'Илгээж байна…' : 'QPay холбуулах хүсэлт илгээх'}
          </button>
          {!selectedAccountId && accounts.length > 0 && (
            <div className="text-[11px] text-slate-400">Хүсэлт илгээх дансаа сонгоно уу</div>
          )}
        </>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { isMobile: mobile } = useShellUi();
  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={'mx-auto flex flex-col gap-4 ' + (mobile ? 'p-4 pb-24' : 'p-6 max-w-[860px]')}>
        <h1 className="text-[20px] font-extrabold text-slate-900">Тохиргоо</h1>
        <PlanPanel />
        <ServicesPanel />
        <WashersPanel />
        <QpayPanel />
      </div>
    </div>
  );
}
